import { useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Download, Cookie, ArrowLeft } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { COOKIE_LAST_UPDATE } from "@/config/legalMeta";
import PrivacyRegistry from "@/components/PrivacyRegistry";
import { printPolicyPage } from "@/lib/printPolicy";

const handleDownload = (title) => printPolicyPage({ title, contentElementId: "policy-content" });

export default function CookiePolicyPage() {
  const [params] = useSearchParams();
  useEffect(() => {
    if (params.get("download") === "1") {
      const timer = setTimeout(() => handleDownload("Cookie Policy - Colucci Assicuratori"), 600);
      return () => clearTimeout(timer);
    }
  }, [params]);
  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="cookie-policy-page">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 py-12">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="text-sm text-[#4A5E51] hover:text-[#163321] flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Torna alla Home
            </Link>
            <Button onClick={() => handleDownload("Cookie Policy - Colucci Assicuratori")} variant="outline" className="rounded-xl border-[#E5E2DB] text-sm gap-2" data-testid="download-cookie-btn">
              <Download className="w-4 h-4" /> Scarica PDF
            </Button>
          </div>

          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-[#D97B58]/10 rounded-full flex items-center justify-center">
              <Cookie className="w-6 h-6 text-[#D97B58]" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-medium text-[#0F1F14]">Cookie Policy</h1>
              <p className="text-xs text-[#4A5E51] mt-0.5" data-testid="cookie-last-update">
                Ultima revisione: <strong>{COOKIE_LAST_UPDATE}</strong>
              </p>
              <p className="text-xs text-[#4A5E51]">Ultimo aggiornamento: {new Date().toLocaleDateString("it-IT")}</p>
            </div>
          </div>

          {/* REGISTRO DINAMICO COOKIE + RESPONSABILI */}
          <div className="mb-10 pb-10 border-b border-[#E5E2DB]">
            <PrivacyRegistry showCookies={true} />
          </div>

          <div id="policy-content" className="prose prose-sm max-w-none text-[#4A5E51] [&_h2]:font-heading [&_h2]:text-base [&_h2]:font-medium [&_h2]:text-[#0F1F14] [&_h2]:mt-8 [&_h2]:mb-3 [&_h3]:font-heading [&_h3]:text-sm [&_h3]:font-medium [&_h3]:text-[#0F1F14] [&_h3]:mt-5 [&_h3]:mb-2 [&_p]:text-sm [&_p]:leading-relaxed [&_p]:mb-3 [&_ul]:text-sm [&_ul]:mb-3 [&_li]:mb-1 [&_table]:text-xs [&_table]:my-4 [&_th]:bg-[#F5F4F0] [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-medium [&_th]:text-[#0F1F14] [&_td]:px-3 [&_td]:py-2 [&_td]:border [&_td]:border-[#E5E2DB] [&_th]:border [&_th]:border-[#E5E2DB]">

            <h2>1. Cosa sono i Cookie</h2>
            <p>I cookie sono piccoli file di testo che i siti web visitati inviano al browser dell'utente, dove vengono memorizzati per essere ritrasmessi agli stessi siti in occasione di visite successive. Il presente sito utilizza un numero estremamente limitato di cookie, tutti di natura tecnica.</p>

            <h2>2. Cookie Utilizzati dal Sito</h2>
            <p>Di seguito l'elenco <strong>completo ed esaustivo</strong> di tutti i cookie impostati dal sito colucciassicuratori.com:</p>

            <h3>2.1 Cookie Tecnici (Strettamente Necessari)</h3>
            <table>
              <thead>
                <tr><th>Nome</th><th>Tipo</th><th>Finalita'</th><th>Durata</th><th>HttpOnly</th><th>SameSite</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>access_token</code></td>
                  <td>Prima parte</td>
                  <td>Autenticazione dell'amministratore al pannello gestionale. Contiene un token JWT crittografato. Non viene impostato per gli utenti pubblici del sito.</td>
                  <td>2 ore</td>
                  <td>Si' (non accessibile da JavaScript)</td>
                  <td>Lax</td>
                </tr>
                <tr>
                  <td><code>refresh_token</code></td>
                  <td>Prima parte</td>
                  <td>Rinnovo automatico della sessione amministrativa senza necessita' di reinserire le credenziali. Non viene impostato per gli utenti pubblici del sito.</td>
                  <td>7 giorni</td>
                  <td>Si' (non accessibile da JavaScript)</td>
                  <td>Lax</td>
                </tr>
              </tbody>
            </table>

            <div className="bg-[#163321]/5 border border-[#163321]/10 rounded-xl p-4 my-4">
              <p className="text-sm text-[#0F1F14] font-medium mb-1">Nota Importante</p>
              <p className="text-xs">Questi cookie vengono impostati <strong>esclusivamente</strong> quando un amministratore effettua il login al pannello di gestione (area riservata /admin). Gli utenti che visitano il sito pubblico e compilano i questionari <strong>non ricevono alcun cookie</strong>.</p>
            </div>

            <h3>2.2 Cookie di Terze Parti</h3>
            <table>
              <thead>
                <tr><th>Servizio</th><th>Fornitore</th><th>Quando si attivano</th><th>Finalita'</th><th>Policy del fornitore</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td>Stripe Checkout</td>
                  <td>Stripe, Inc.</td>
                  <td>Solo quando l'utente procede al pagamento di una consulenza e viene reindirizzato su checkout.stripe.com</td>
                  <td>Elaborazione sicura del pagamento, prevenzione frodi</td>
                  <td><a href="https://stripe.com/it/privacy" target="_blank" rel="noopener noreferrer" className="text-[#163321] underline">stripe.com/privacy</a></td>
                </tr>
              </tbody>
            </table>

            <h2>3. Cookie NON Utilizzati</h2>
            <p>Per trasparenza, confermiamo che il presente sito <strong>NON utilizza</strong>:</p>
            <ul>
              <li>Cookie di profilazione</li>
              <li>Cookie di marketing o remarketing</li>
              <li>Google Analytics o altri sistemi con cookie di tracciamento</li>
              <li>Facebook Pixel o altri pixel di tracciamento</li>
              <li>Cookie di social media (pulsanti like/share)</li>
              <li>Cookie pubblicitari di qualsiasi tipo</li>
              <li>Local Storage o Session Storage per dati personali</li>
              <li>Fingerprinting del browser</li>
            </ul>

            <h2>3-bis. Plausible Analytics (dichiarazione preparatoria)</h2>
            <p>
              Colucci Assicuratori sta valutando l&apos;attivazione di <strong>Plausible Analytics</strong>
              come strumento di analisi web privacy-friendly per misurare in forma aggregata e anonima
              statistiche di visita (pagine viste, provenienza generica, tempo di permanenza).
              <strong> Al momento della presente informativa, Plausible non è attivo</strong> sul sito:
              nessuno script di tracciamento viene caricato dalle pagine.
            </p>
            <p>
              In caso di attivazione futura, l&apos;utente verrà informato tramite aggiornamento
              di questa Cookie Policy (con bump della versione consenso, che riattiva il banner)
              prima dell&apos;inizio della raccolta dati. Caratteristiche di Plausible (se attivato):
            </p>
            <ul>
              <li><strong>Non utilizza cookie</strong> di tracciamento</li>
              <li><strong>Non raccoglie dati personali identificabili</strong></li>
              <li><strong>Non profila l&apos;utente</strong> e non traccia il comportamento tra siti diversi</li>
              <li>Sito ufficiale: <a href="https://plausible.io/data-policy" target="_blank" rel="noopener noreferrer">plausible.io/data-policy</a></li>
            </ul>

            <h2>4. Altre Tecnologie di Archiviazione</h2>
            <p>Il sito non utilizza Local Storage, Session Storage ne' altre tecnologie di archiviazione lato client per memorizzare dati personali degli utenti. Tutti i dati inseriti nei questionari vengono inviati direttamente al server e non vengono memorizzati nel browser.</p>

            <h2>5. Servizi Esterni Utilizzati (senza cookie)</h2>
            <p>I seguenti servizi esterni vengono utilizzati dal sito <strong>senza impostare cookie</strong> sul browser dell'utente:</p>
            <table>
              <thead>
                <tr><th>Servizio</th><th>Utilizzo</th><th>Cookie</th></tr>
              </thead>
              <tbody>
                <tr><td>Spoki (WhatsApp Business)</td><td>Notifiche automatiche via WhatsApp (invocato lato server)</td><td>Nessuno (server-to-server)</td></tr>
                <tr><td>Blue2Drive API</td><td>Catalogo auto a noleggio (proxy lato server)</td><td>Nessuno (server-to-server)</td></tr>
                <tr><td>Emergent Object Storage</td><td>Archiviazione sicura documenti caricati</td><td>Nessuno (server-to-server)</td></tr>
                <tr><td>Unsplash</td><td>Immagini stock nella sezione "Chi Siamo"</td><td>Possibili cookie tecnici dal CDN di Unsplash</td></tr>
              </tbody>
            </table>

            <h2>6. Gestione dei Cookie</h2>
            <p>Poiche' il sito utilizza esclusivamente cookie tecnici strettamente necessari (e solo per l'area amministrativa), <strong>non e' richiesto il consenso preventivo</strong> ai sensi dell'art. 122 del Codice Privacy e delle Linee Guida del Garante del 10 giugno 2021.</p>
            <p>L'utente puo' comunque gestire i cookie attraverso le impostazioni del proprio browser:</p>
            <ul>
              <li><strong>Chrome</strong>: Impostazioni &gt; Privacy e sicurezza &gt; Cookie e altri dati dei siti</li>
              <li><strong>Firefox</strong>: Impostazioni &gt; Privacy e sicurezza &gt; Cookie e dati dei siti web</li>
              <li><strong>Safari</strong>: Preferenze &gt; Privacy &gt; Gestisci dati siti web</li>
              <li><strong>Edge</strong>: Impostazioni &gt; Cookie e autorizzazioni sito &gt; Cookie e dati del sito</li>
            </ul>

            <h2>7. Base Normativa</h2>
            <p>La presente Cookie Policy e' redatta in conformita' a:</p>
            <ul>
              <li>Regolamento UE 2016/679 (GDPR)</li>
              <li>D.Lgs. 196/2003 (Codice Privacy) come modificato dal D.Lgs. 101/2018</li>
              <li>Direttiva 2002/58/CE (Direttiva ePrivacy)</li>
              <li>Linee Guida del Garante Privacy del 10 giugno 2021 sui cookie e altri strumenti di tracciamento</li>
            </ul>

            <h2>7-bis. Versioning del consenso</h2>
            <p>
              Le preferenze di consenso espresse dall'utente vengono registrate localmente nel browser con un identificativo di versione (campo <code>version</code>).
              Quando il presente documento o i meccanismi tecnici dei cookie subiscono modifiche significative, viene incrementata la versione del consenso e il banner viene mostrato nuovamente all'utente per acquisire un consenso informato e aggiornato. Versione corrente del consenso: <strong>2026.05.05</strong> (allineata all'aggiornamento Privacy Policy che descrive la cifratura AES-256-GCM dei dati sanitari del prodotto RiS).
            </p>
            <p>
              L'utente può in ogni momento revocare o modificare le preferenze cliccando il pulsante "scudo" in basso a sinistra ("Gestisci Privacy"), che riapre il banner consentendo una nuova selezione.
            </p>

            <h2>8. Contatti</h2>
            <p>Per qualsiasi domanda relativa all'uso dei cookie, contattare:</p>
            <p>
              <strong>CHRISTIAN E CIRO COLUCCI S.A.S.</strong><br />
              Email: <a href="mailto:info@colucciassicuratori.com" className="text-[#163321] underline">info@colucciassicuratori.com</a><br />
              Tel: +39 081 7611000
            </p>

          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
